import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ReportTemplate } from "src/types";

interface TemplateOrderProps {
  template: ReportTemplate;
  setTemplate: (newTemplate: Partial<ReportTemplate>) => void;
}

interface SortableItemProps {
  id: string;
  page: any;
}

function SortableItem({ id, page }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const map = {
    cover: "Cover",
    longevity: "Longevity",
    incomes: "Income",
    spending: "Spending",
    "income-chart": "Income Chart",
    "spending-chart": "Spending Chart",
    "by-person": "Income By Person",
    "by-income-type": "Income By Type",
    "by-tax-status": "Income By Tax Status",
  } as any;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center p-3 bg-white border border-gray-200 rounded-lg shadow-sm cursor-move hover:shadow-md transition-shadow"
    >
      <div className="flex items-center space-x-3">
        <div className="flex flex-col space-y-1">
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
        </div>
        <span className="text-sm font-medium text-gray-700 capitalize">
          {map[page.name] || page.name}
        </span>
      </div>
    </div>
  );
}

const TemplateOrder: React.FC<TemplateOrderProps> = ({
  template,
  setTemplate,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = template.settings.findIndex(
        (page) => page.id === active.id,
      );
      const newIndex = template.settings.findIndex(
        (page) => page.id === over.id,
      );

      const newSettings = arrayMove(template.settings, oldIndex, newIndex);

      setTemplate({
        settings: newSettings,
      });
    }
  }

  return (
    <div className="w-64 px-4 rounded-lg">
      <h3 className="text-2xl font-semibold text-gray-800 mb-4">Page Order</h3>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={template.settings.map((page) => page.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {template.settings.map((page) => (
              <SortableItem key={page.id} id={page.id} page={page} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default TemplateOrder;
